import { Component, OnInit, OnDestroy, AfterViewChecked, ViewChild, ElementRef, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Sortable from 'sortablejs';
import { GetSiteGQL, GetProfilesGQL, DeleteProfileGQL, ReorderProfilesGQL } from '../../graphql/graphql';
import { ToastService } from '../../shared/toast/toast.service';

@Component({
    selector: 'app-profiles',
    templateUrl: './profiles.component.html',
    styleUrls: ['./profiles.component.scss'],
    standalone: false
})
export class ProfilesComponent implements OnInit, OnDestroy, AfterViewChecked {
  siteUrl: string = '';
  siteId: string = '';
  profiles: any[] = [];
  loading = true;
  showDeleteModal = false;
  profileToDelete: any = null;
  viewMode: 'list' | 'cards' = 'list';
  private sortableInstance: Sortable | null = null;
  private currentSortableEl: HTMLElement | null = null;

  @ViewChild('listContainer') listContainer?: ElementRef<HTMLElement>;
  @ViewChild('cardsContainer') cardsContainer?: ElementRef<HTMLElement>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private getSiteGQL: GetSiteGQL,
    private getProfilesGQL: GetProfilesGQL,
    private deleteProfileGQL: DeleteProfileGQL,
    private reorderProfilesGQL: ReorderProfilesGQL,
    private toast: ToastService,
    private ngZone: NgZone
  ) {}

  trackById(_index: number, profile: any): string {
    return profile._id;
  }

  ngOnInit() {
    this.siteUrl = this.route.parent?.snapshot.params['url'] || '';
    this.loadSiteData();
  }

  loadSiteData() {
    if (this.siteUrl) {
      this.getSiteGQL.watch({ variables: { url: this.siteUrl, categoryId: '' } }).valueChanges.subscribe(result => {
        if (result.data?.site?._id) {
          this.siteId = result.data.site._id;
          this.loadProfiles();
        }
      });
    }
  }

  loadProfiles() {
    if (this.siteId) {
      this.getProfilesGQL.watch({ variables: { siteId: this.siteId }, fetchPolicy: 'network-only' }).valueChanges.subscribe(result => {
        if (result.data?.profiles) {
          this.profiles = result.data.profiles.map(p => ({ ...p }));
        }
        this.loading = false;
      });
    }
  }

  addProfile() {
    this.router.navigate(['profile'], { relativeTo: this.route });
  }

  editProfile(profileId: string) {
    this.router.navigate(['profile', profileId], { relativeTo: this.route });
  }

  confirmDelete(profile: any) {
    this.profileToDelete = profile;
    this.showDeleteModal = true;
  }

  cancelDelete() {
    this.showDeleteModal = false;
    this.profileToDelete = null;
  }

  deleteProfile() {
    if (this.profileToDelete) {
      this.deleteProfileGQL.mutate({
        variables: { id: this.profileToDelete._id },
        refetchQueries: [{ query: this.getProfilesGQL.document, variables: { siteId: this.siteId } }]
      }).subscribe({
        next: () => {
          this.showDeleteModal = false;
          this.profileToDelete = null;
          this.toast.success('Profile deleted');
        },
        error: (err) => { console.error('Error deleting profile:', err); this.toast.error('Error deleting profile'); }
      });
    }
  }

  ngAfterViewChecked() {
    const el = this.viewMode === 'list'
      ? this.listContainer?.nativeElement
      : this.cardsContainer?.nativeElement;

    if (el && el !== this.currentSortableEl) {
      this.destroySortable();
      this.currentSortableEl = el;
      this.sortableInstance = Sortable.create(el, {
        animation: 150,
        handle: '.drag-handle',
        ghostClass: 'sortable-ghost',
        onEnd: (evt) => {
          const oldIndex = evt.oldIndex!;
          const newIndex = evt.newIndex!;
          if (oldIndex === newIndex) return;

          // Revert the DOM move so Angular stays in control via ngFor+trackBy
          const parent = evt.from;
          const movedEl = evt.item;
          if (evt.oldIndex! < evt.newIndex!) {
            parent.insertBefore(movedEl, parent.children[oldIndex]);
          } else {
            parent.insertBefore(movedEl, parent.children[oldIndex + 1]);
          }

          // Now update the data model — Angular will reconcile the DOM
          this.ngZone.run(() => {
            const [moved] = this.profiles.splice(oldIndex, 1);
            this.profiles.splice(newIndex, 0, moved);

            const input = this.profiles.map((p, i) => ({ id: p._id, order: i }));
            this.reorderProfilesGQL.mutate({
              variables: { input },
              refetchQueries: [{ query: this.getProfilesGQL.document, variables: { siteId: this.siteId } }]
            }).subscribe({
              error: (err) => console.error('Error reordering profiles:', err)
            });
          });
        }
      });
    } else if (!el) {
      this.destroySortable();
    }
  }

  ngOnDestroy() {
    this.destroySortable();
  }

  private destroySortable() {
    this.sortableInstance?.destroy();
    this.sortableInstance = null;
    this.currentSortableEl = null;
  }
}
